import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  Typography,
  Chip,
} from "@mui/material";
import { apiService } from "../../config/api";
import { WeeklyLogService } from "../../services/weeklyLogService";

interface WeeklyLogPanelProps {
  studyId: string;
  researcherId: string | null;
  showSuccess: (title: string, message: string, context?: string) => void;
  showError: (title: string, message: string, context?: string) => void;
}

const WeeklyLogPanel: React.FC<WeeklyLogPanelProps> = ({
  studyId,
  researcherId,
  showSuccess,
  showError,
}) => {
  const [phoneMinutes, setPhoneMinutes] = useState<number | "">("");
  const [emailMinutes, setEmailMinutes] = useState<number | "">("");
  const [notes, setNotes] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [weekStartIso, setWeekStartIso] = useState<string>("");
  const [weekEndIso, setWeekEndIso] = useState<string>("");
  const [weekLabel, setWeekLabel] = useState<string>("");

  const offPlatformTotal =
    Number(phoneMinutes || 0) + Number(emailMinutes || 0);
  const researcherTime = offPlatformTotal; // renamed for clarity
  const platformTime = 0; // placeholder until platform time is implemented
  const totalWeeklyMinutes = researcherTime + platformTime;
  const avgDaily = Number((totalWeeklyMinutes / 7).toFixed(2));

  const computeWeekRange = () => {
    // Get local time, convert to UTC, then calculate previous Monday 00:00 and next Sunday 23:59 (UTC)
    const localNow = new Date();
    const utcNow = new Date(
      localNow.getTime() + localNow.getTimezoneOffset() * 60000
    );

    // Calculate days since Monday (Monday=1, Sunday=0)
    const day = utcNow.getUTCDay();
    const daysSinceMonday = (day + 6) % 7; // 0 if Monday

    const mondayUtc = new Date(
      Date.UTC(
        utcNow.getUTCFullYear(),
        utcNow.getUTCMonth(),
        utcNow.getUTCDate() - daysSinceMonday,
        0,
        0,
        0
      )
    );

    const sundayUtc = new Date(
      Date.UTC(
        mondayUtc.getUTCFullYear(),
        mondayUtc.getUTCMonth(),
        mondayUtc.getUTCDate() + 6,
        23,
        59,
        59
      )
    );

    setWeekStartIso(mondayUtc.toISOString());
    setWeekEndIso(sundayUtc.toISOString());

    const opts: Intl.DateTimeFormatOptions = {
      weekday: "long",
      day: "numeric",
      month: "short",
      year: "numeric",
    };
    const mondayLabel = mondayUtc.toLocaleDateString(undefined, opts);
    const sundayLabel = sundayUtc.toLocaleDateString(undefined, opts);
    setWeekLabel(`${mondayLabel} to ${sundayLabel}`);
  };

  useEffect(() => {
    computeWeekRange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch existing log for this study+week if any
  useEffect(() => {
    const fetchExisting = async () => {
      if (!weekStartIso || !weekEndIso) return;
      setLoading(true);
      try {
        const token = await apiService.getAuthToken();
        const resp: any = await WeeklyLogService.getWeeklyLog(
          token,
          studyId,
          weekStartIso,
          weekEndIso
        );
        if (resp && resp.success && resp.data) {
          const data = resp.data;
          setPhoneMinutes(data.phoneMinutes ?? 0);
          setEmailMinutes(data.emailMinutes ?? 0);
          setNotes(data.notes ?? "");
        }
      } catch (err: any) {
        showError(
          "Load Weekly Log",
          err?.message || "Failed to load weekly log",
          "Weekly Log"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchExisting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStartIso, weekEndIso, studyId]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = await apiService.getAuthToken();
      const payload = {
        studyId,
        startDate: weekStartIso,
        endDate: weekEndIso,
        phoneMinutes: Number(phoneMinutes || 0),
        emailMinutes: Number(emailMinutes || 0),
        offPlatformTotal: researcherTime,
        onPlatformMinutes: platformTime,
        totalWeeklyMinutes,
        avgDailyMinutes: avgDaily,
        notes: notes || null,
      };

      const res: any = await WeeklyLogService.saveWeeklyLog(token, payload);
      if (res && res.success) {
        showSuccess(
          "Weekly Log Saved",
          "Your weekly log was saved successfully",
          "Weekly Log"
        );
      } else {
        throw new Error(res?.error || "Failed to save weekly log");
      }
    } catch (err: any) {
      showError(
        "Save Weekly Log",
        err?.message || "Failed to save weekly log",
        "Weekly Log"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: "auto" }}>
      {/* Week Header */}
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, mb: 0.5, color: "primary.main" }}
        >
          Weekly Log
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: "#666" }}>
          {weekLabel}
        </Typography>
        <Box
          sx={{
            height: 4,
            width: 80,
            background: "linear-gradient(90deg, #de9b9bff, #f6c2c2ff)",
            borderRadius: 2,
            mx: "auto",
          }}
        />
      </Box>

      {/* Platform Time - Coming Soon */}
      <Card
        sx={{
          mb: 3,
          backgroundColor: "#fff3e0",
          borderLeft: "4px solid #d32f2f",
        }}
      >
        <CardContent sx={{ py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Platform Time{" "}
            <Chip
              label="Coming soon"
              size="small"
              variant="outlined"
              sx={{ ml: 1 }}
            />
          </Typography>
          <Typography variant="h6" sx={{ mt: 1 }}>
            — min
          </Typography>
        </CardContent>
      </Card>

      {/* Researcher Time Input */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Researcher Time
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              label="Phone Calls"
              type="number"
              inputProps={{ min: 0 }}
              value={phoneMinutes}
              onChange={(e) =>
                setPhoneMinutes(
                  e.target.value === "" ? "" : Number(e.target.value)
                )
              }
              size="small"
              placeholder="0"
              helperText="minutes"
            />
            <TextField
              label="Email Writing"
              type="number"
              inputProps={{ min: 0 }}
              value={emailMinutes}
              onChange={(e) =>
                setEmailMinutes(
                  e.target.value === "" ? "" : Number(e.target.value)
                )
              }
              size="small"
              placeholder="0"
              helperText="minutes"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Time Summary */}
      <Card
        sx={{
          mb: 3,
          background: "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)",
          borderTop: "4px solid #d32f2f",
        }}
      >
        <CardContent>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  textTransform: "uppercase",
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                Total Researcher Time
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                {researcherTime}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                min
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  textTransform: "uppercase",
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                Weekly Average
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                {avgDaily}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                min/day
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Total Time */}
      <Card sx={{ mb: 3, border: "2px solid #d32f2f" }}>
        <CardContent>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textTransform: "uppercase", fontSize: 11, fontWeight: 600 }}
          >
            Total Time This Week
          </Typography>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mt: 1, color: "#d32f2f" }}
          >
            {totalWeeklyMinutes}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            minutes
          </Typography>
        </CardContent>
      </Card>

      {/* Notes */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Notes
        </Typography>
        <TextField
          placeholder="Add any notes about this week (optional)"
          multiline
          fullWidth
          minRows={3}
          maxRows={6}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          variant="outlined"
          size="small"
        />
      </Box>

      {/* Actions */}
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          size="large"
          sx={{ flex: 1 }}
        >
          {loading ? "Saving..." : "Save Weekly Log"}
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            computeWeekRange();
            setPhoneMinutes("");
            setEmailMinutes("");
            setNotes("");
          }}
          disabled={loading}
          size="large"
        >
          Reset
        </Button>
      </Box>
    </Box>
  );
};

export default WeeklyLogPanel;
